// Charts/QuantityByCategoryChart.tsx
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from '../SalesByCategoryCharts.module.scss';
import { Spinner } from 'components/UI';
import type { SalesByCategoryReport } from 'model/SalesByCategoryReport';
import { mapSalesByCategoryToSlices } from '../utils/salesByCategoryChartMapper';

interface Props {
    loading: boolean;
    report: SalesByCategoryReport | null;
}

export const QuantityByCategoryChart: React.FC<Props> = ({ loading, report }) => {
    const { data, total } = React.useMemo(() => mapSalesByCategoryToSlices(report, 'quantity'), [report]);

    return (
        <div className={styles.card}>
            <div className={styles.headerRow}>
                <h3 className={styles.title}>Quantity by Category</h3>
            </div>

            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <>
                        <div className={styles.pieWrap}>
                            <ResponsiveContainer
                                width="100%"
                                height={200}
                            >
                                <PieChart>
                                    <Pie
                                        data={data}
                                        dataKey="value"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        startAngle={90}
                                        endAngle={450}
                                        stroke="none"
                                    >
                                        {data.map((s, i) => (
                                            <Cell
                                                key={i}
                                                fill={s.color}
                                            />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>

                            <div className={styles.centerLabel}>
                                <div className={styles.totalValue}>{total}</div>
                                <div className={styles.totalPercentage}>{total > 0 ? '100%' : '0%'}</div>
                            </div>
                        </div>

                        <div className={styles.legend}>
                            {data.map((s, i) => (
                                <div
                                    key={i}
                                    className={styles.legendItem}
                                >
                                    <div
                                        className={styles.legendDot}
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <span className={styles.legendLabel}>{s.name}</span>
                                    <span className={styles.legendValue}>
                                        {s.value} ({s.percentage}%)
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QuantityByCategoryChart;
